import { makeAutoObservable } from "mobx";
/**
 * Утилита для безопасного получения строкового ключа
 */
export const getItemKey = (item, rowKey) => {
    return typeof rowKey === "function" ? rowKey(item) : String(item[rowKey]);
};
export class DataTableStore {
    constructor(options) {
        // Состояние
        this.data = [];
        this.loading = false;
        this.error = null;
        this.currentPage = 1;
        this.pageSize = 10;
        this.total = 0;
        this.selectedRowKeys = [];
        this.filters = {};
        this.sorters = {};
        // Конфигурация
        this.defaultFilters = {};
        // Загрузчик данных
        this.dataLoader = null;
        // Номер последнего запроса: ответ более раннего запроса отбрасывается (WP-DSP-FIX-03).
        // DataTable при монтировании грузит без фильтров, страница затем делает reset() и грузит с фильтром —
        // без этого побеждал ответ, пришедший последним («Заказы без маршрутов» показывали все заказы).
        this.requestSeq = 0;
        this.columns = [];
        this.rowKey = "id";
        // Действия
        this.setColumns = (columns) => {
            this.columns = columns;
        };
        this.setRowKey = (rowKey) => {
            this.rowKey = rowKey;
        };
        this.setData = (data) => {
            this.data = data;
            this.total = data.length;
        };
        this.setLoading = (loading) => {
            this.loading = loading;
        };
        this.setError = (error) => {
            this.error = error;
        };
        this.setCurrentPage = (page) => {
            this.currentPage = page;
        };
        this.setPageSize = (size) => {
            this.pageSize = size;
        };
        this.setTotal = (total) => {
            this.total = total;
        };
        this.setSelectedRowKeys = (keys) => {
            this.selectedRowKeys = keys;
        };
        this.setFilters = (filters) => {
            // Если передан пустой объект, сбрасываем к defaultFilters
            const newFilters = Object.keys(filters).length === 0
                ? { ...this.defaultFilters }
                : { ...this.filters, ...filters };
            this.filters = newFilters;
            this.setCurrentPage(1);
        };
        this.setSorters = (sorters) => {
            this.sorters = sorters;
            this.setCurrentPage(1);
            // Передаем новые параметры сортировки напрямую в loadData
            this.loadData({
                sorters: { ...sorters },
                page: this.currentPage,
                pageSize: this.pageSize,
                filters: this.filters,
            }); // Создаем копию объекта
        };
        this.clearSelection = () => {
            this.setSelectedRowKeys([]);
        };
        this.select = (key) => {
            const isEmpty = key === undefined;
            if (isEmpty) {
                this.clearSelection();
            }
            else {
                const isNewKey = !this.selectedRowKeysSet.has(key);
                if (isNewKey) {
                    // Добавляем новый ключ к уже выбранным (могут быть на другой странице)
                    const allSelectedValues = [...this.selectedRowKeys.map(String), key];
                    this.setSelectedRowKeys(allSelectedValues);
                }
                else {
                    // Если ключ уже выбран, удаляем его
                    this.deselect(key);
                }
            }
        };
        this.selectAll = (selected, _selectedRows, changeRows) => {
            if (selected) {
                const allSelectedValues = [
                    ...this.selectedRowKeys.map(String),
                    ...changeRows.map((record) => getItemKey(record, this.rowKey)),
                ];
                const newSelectedValues = Array.from(new Map(allSelectedValues.map((k) => [k, k])).values());
                this.setSelectedRowKeys(newSelectedValues);
            }
            else {
                const changedSet = new Set(changeRows.map((record) => getItemKey(record, this.rowKey)));
                const newSelectedValues = this.selectedRowKeys
                    .filter((key) => !changedSet.has(key))
                    .map(String);
                this.setSelectedRowKeys(newSelectedValues);
            }
        };
        this.deselect = (key) => {
            const filteredValue = this.selectedRowKeys
                .filter((value) => value !== key)
                .map(String);
            this.setSelectedRowKeys(filteredValue);
        };
        // Обработчик изменений таблицы (пагинация, фильтры, сортировка)
        this.handleTableChange = (pagination, filters, sorters) => {
            // Собираем все параметры для одного вызова loadData
            let newSorters = { ...this.sorters };
            let newFilters = { ...this.filters };
            let newPage = this.currentPage;
            let newPageSize = this.pageSize;
            // Обрабатываем сортировку
            if (sorters) {
                const sortersArray = Array.isArray(sorters) ? sorters : [sorters];
                // Преобразуем SorterResult в Record<string, any>
                const sortersRecord = {};
                sortersArray.forEach((sorter) => {
                    if (sorter.field && sorter.order) {
                        sortersRecord[String(sorter.field)] = sorter.order;
                    }
                });
                newSorters = sortersRecord;
            }
            // Обрабатываем фильтры
            if (filters) {
                newFilters = { ...this.filters, ...filters };
            }
            // Обрабатываем пагинацию
            if (pagination) {
                if (pagination.current && pagination.current !== this.currentPage) {
                    newPage = pagination.current;
                }
                if (pagination.pageSize && pagination.pageSize !== this.pageSize) {
                    newPageSize = pagination.pageSize;
                    newPage = 1; // Сброс на первую страницу при изменении размера
                }
            }
            // Обновляем состояние
            this.sorters = newSorters;
            this.filters = newFilters;
            this.setCurrentPage(newPage);
            this.setPageSize(newPageSize);
            // Вызываем loadData один раз со всеми параметрами
            this.loadData({
                sorters: newSorters,
                filters: newFilters,
                page: newPage,
                pageSize: newPageSize,
            });
        };
        this.clearError = () => {
            this.error = null;
        };
        this.refresh = async () => {
            this.loadData(this.params);
        };
        this.reset = () => {
            this.requestSeq += 1;
            this.data = [];
            this.loading = false;
            this.error = null;
            this.setCurrentPage(1);
            this.setPageSize(10);
            this.total = 0;
            this.selectedRowKeys = [];
            this.filters = { ...this.defaultFilters };
            this.sorters = {};
        };
        // Инициализация загрузчика данных
        this.initializeDataLoader = (options) => {
            this.dataLoader = options;
            // Устанавливаем начальные параметры
            if (options.initialParams) {
                if (options.initialParams.page)
                    this.setCurrentPage(options.initialParams.page);
                if (options.initialParams.pageSize)
                    this.setPageSize(options.initialParams.pageSize);
                if (options.initialParams.filters)
                    this.filters = {
                        ...this.defaultFilters,
                        ...options.initialParams.filters,
                    };
                if (options.initialParams.sorters)
                    this.sorters = options.initialParams.sorters;
            }
        };
        // Принудительная загрузка данных (для инициализации)
        this.initializeData = () => {
            if (this.dataLoader && !this.loading) {
                this.loadData();
            }
        };
        // Методы для работы с выбранными строками
        this.selectRow = (key, selected) => {
            if (selected) {
                this.selectedRowKeys = [...this.selectedRowKeys, key];
            }
            else {
                this.selectedRowKeys = this.selectedRowKeys.filter((k) => k !== key);
            }
        };
        this.selectAllRows = (selected) => {
            if (selected) {
                this.selectedRowKeys = this.data.map((item) => item.id || item.key);
            }
            else {
                this.selectedRowKeys = [];
            }
        };
        // Методы для работы с фильтрами
        this.addFilter = (key, value) => {
            this.setFilters({ [key]: value });
        };
        this.removeFilter = (key) => {
            const newFilters = { ...this.filters };
            delete newFilters[key];
            this.filters = newFilters;
            this.setCurrentPage(1);
        };
        // Методы для работы с сортировкой
        this.addSorter = (key, order) => {
            if (order) {
                this.setSorters({ [key]: order });
            }
            else {
                const newSorters = { ...this.sorters };
                delete newSorters[key];
                this.setSorters(newSorters);
            }
        };
        // Загрузка данных
        this.loadData = async (params) => {
            if (!this.dataLoader) {
                throw new Error("DataLoader не инициализирован");
            }
            // Защита от повторных вызовов во время загрузки
            if (this.loading) {
                return;
            }
            // Обновляем параметры если переданы
            if (params?.page !== undefined)
                this.setCurrentPage(params.page);
            if (params?.pageSize !== undefined)
                this.setPageSize(params.pageSize);
            if (params?.filters !== undefined)
                this.filters = { ...this.filters, ...params.filters };
            if (params?.sorters !== undefined)
                this.sorters = params.sorters;
            const seq = ++this.requestSeq;
            this.setLoading(true);
            this.setError(null);
            try {
                // Используем переданные параметры или текущие значения
                const finalSorters = params?.sorters || this.sorters;
                const result = await this.dataLoader.loadData({
                    page: this.currentPage,
                    pageSize: this.pageSize,
                    filters: this.filters,
                    sorters: finalSorters,
                });
                if (seq !== this.requestSeq)
                    return;
                this.setData(result.data);
                this.setTotal(result.total);
                this.setCurrentPage(result.page);
                this.setPageSize(result.pageSize);
            }
            catch (error) {
                if (seq !== this.requestSeq)
                    return;
                this.setError(error instanceof Error ? error.message : "Ошибка загрузки данных");
            }
            finally {
                if (seq === this.requestSeq)
                    this.setLoading(false);
            }
        };
        this.defaultFilters = options?.defaultFilters || {};
        this.pageSize = options?.initialPageSize || 10;
        // Инициализируем загрузчик данных
        if (options?.dataLoader) {
            this.initializeDataLoader(options.dataLoader);
        }
        makeAutoObservable(this);
    }
    // Геттеры
    get selectedRowKeysSet() {
        return new Set(this.selectedRowKeys);
    }
    get params() {
        return {
            page: this.currentPage,
            pageSize: this.pageSize,
            filters: this.filters,
            sorters: this.sorters,
        };
    }
}
